import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { MessageCircle, X, Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * FloatingChatButton Component
 * Attractive floating button with animated tooltip pop message
 */
const FloatingChatButton = ({ onClick, isAvailable }) => {
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Show the tooltip bubble after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!dismissed) {
        setShowBubble(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [dismissed]);

  // Auto-hide and re-show the bubble periodically
  useEffect(() => {
    if (!showBubble || dismissed) return;

    const hideTimer = setTimeout(() => {
      setShowBubble(false);
      // Re-show after a pause
      const reshowTimer = setTimeout(() => {
        if (!dismissed) setShowBubble(true);
      }, 30000);
      return () => clearTimeout(reshowTimer);
    }, 8000);

    return () => clearTimeout(hideTimer);
  }, [showBubble, dismissed]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setShowBubble(false);
    setDismissed(true);
  };

  const handleClick = () => {
    setShowBubble(false);
    setDismissed(true);
    onClick();
  };

  return (
    <div className="relative">
      {/* Tooltip Pop Message */}
      <div
        className={cn(
          "absolute bottom-full right-0 mb-3 transition-all duration-500 ease-out origin-bottom-right",
          showBubble && !dismissed
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-90 translate-y-2 pointer-events-none"
        )}
      >
        <div className="relative bg-white rounded-2xl shadow-xl border border-primary/15 px-4 py-3 w-[220px] cursor-pointer group"
             onClick={handleClick}
        >
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -left-2 w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors shadow-sm"
          >
            <X className="h-3 w-3 text-gray-500" />
          </button>

          {/* Content */}
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                Got questions?
              </p>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                Our AI Assistant can help!
              </p>
            </div>
          </div>

          {/* Arrow pointing to button */}
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-primary/15 rotate-45 transform" />
        </div>
      </div>

      {/* Ping ring animation */}
      <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '3s' }} />

      {/* Main Button */}
      <Button
        onClick={handleClick}
        size="icon"
        className="w-14 h-14 sm:w-[60px] sm:h-[60px] rounded-full bg-gradient-to-br from-primary via-orange-500 to-orange-600 text-white shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/50 hover:scale-110 active:scale-95 transition-all relative z-10"
      >
        <MessageCircle className="h-6 w-6 sm:h-7 sm:w-7" />
        {/* Online indicator */}
        {isAvailable && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 border-2 border-white" />
          </span>
        )}
      </Button>
    </div>
  );
};

export default FloatingChatButton;
